import React, { useState } from 'react';
import { Plus, Trash2, Loader2, Filter, X, Check, ToggleLeft, ToggleRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useApp } from '@/context/AppContext';
import { CONDITION_FIELDS, CONDITION_OPERATORS, inputClass, inputStyle } from './constants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function RegoleTab() {
  const { rules, customCategories, createRule, updateRule, deleteRule } = useApp();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', condition_field: 'from_addr', condition_operator: 'contains', condition_value: '', action_type: 'set_category', action_value: '', priority: 0 });
  const [applyToAll, setApplyToAll] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [applyingAll, setApplyingAll] = useState(false);
  const field = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error('Inserisci un nome per la regola'); return; }
    if (!form.condition_value.trim()) { toast.error('Inserisci un valore per la condizione'); return; }
    if (!form.action_value) { toast.error('Seleziona una categoria di destinazione'); return; }
    setSaving(true);
    try {
      await createRule(form);
      if (applyToAll) {
        await axios.post(`${API}/rules/apply-all`);
        toast.success('Regola creata e applicata a tutte le email');
      } else {
        toast.success('Regola creata');
      }
      setForm({ name: '', condition_field: 'from_addr', condition_operator: 'contains', condition_value: '', action_type: 'set_category', action_value: '', priority: 0 });
      setShowForm(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nella creazione');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Eliminare la regola "${name}"?`)) return;
    setDeletingId(id);
    try { await deleteRule(id); toast.success('Regola eliminata'); }
    catch { toast.error("Errore nell'eliminazione"); }
    finally { setDeletingId(null); }
  };

  const handleToggle = async (rule) => {
    setTogglingId(rule.id);
    try {
      await updateRule(rule.id, { ...rule, active: !rule.active });
      toast.success(rule.active ? 'Regola disattivata' : 'Regola attivata');
    } catch { toast.error('Errore'); }
    finally { setTogglingId(null); }
  };

  const handleApplyAll = async () => {
    setApplyingAll(true);
    try {
      const res = await axios.post(`${API}/rules/apply-all`);
      toast.success(res.data.message || 'Regole applicate');
    } catch { toast.error("Errore nell'applicazione"); }
    finally { setApplyingAll(false); }
  };

  const getRuleDescription = (rule) => {
    const f = CONDITION_FIELDS.find(x => x.value === rule.condition_field)?.label || rule.condition_field;
    const op = CONDITION_OPERATORS.find(x => x.value === rule.condition_operator)?.label || rule.condition_operator;
    const cat = customCategories.find(c => c.id === rule.action_value);
    return `Se ${f} ${op} ${rule.condition_value} → ${cat?.name || rule.action_value}`;
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">Le regole categorizzano automaticamente le email quando arrivano.</p>
        <div className="flex items-center gap-2">
          {rules.length > 0 && (
            <button data-testid="apply-all-rules-btn" onClick={handleApplyAll} disabled={applyingAll} className="flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-xl border transition-all hover:bg-gray-50" style={{ borderColor: '#D9D2C8' }}>
              {applyingAll ? <Loader2 size={12} className="animate-spin" /> : <Filter size={12} />}
              Applica a tutte le email
            </button>
          )}
          <button data-testid="add-rule-btn" onClick={() => setShowForm(s => !s)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl border transition-all" style={{ borderColor: showForm ? '#E2725B' : '#D9D2C8', background: showForm ? 'rgba(226,114,91,0.08)' : 'white', color: showForm ? '#E2725B' : '#333' }}>
            {showForm ? <X size={14} /> : <Plus size={14} />}
            {showForm ? 'Annulla' : 'Nuova regola'}
          </button>
        </div>
      </div>

      {showForm && (
        <div className="rounded-xl border p-5 space-y-4" style={{ borderColor: '#D9D2C8', background: 'white' }}>
          <h4 className="text-sm font-semibold">Nuova regola</h4>
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Nome regola *</label>
            <input data-testid="new-rule-name" type="text" value={form.name} onChange={e => field('name', e.target.value)} placeholder="Es. Clienti Rossi Architecture" className={inputClass} style={inputStyle} />
          </div>
          <div className="rounded-xl p-4 space-y-3" style={{ background: 'hsl(40,18%,97%)', border: '1px solid #D9D2C8' }}>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Condizione</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Campo</label>
                <select data-testid="rule-condition-field" value={form.condition_field} onChange={e => field('condition_field', e.target.value)} className={inputClass} style={inputStyle}>
                  {CONDITION_FIELDS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Operatore</label>
                <select data-testid="rule-condition-operator" value={form.condition_operator} onChange={e => field('condition_operator', e.target.value)} className={inputClass} style={inputStyle}>
                  {CONDITION_OPERATORS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Valore *</label>
                <input data-testid="rule-condition-value" type="text" value={form.condition_value} onChange={e => field('condition_value', e.target.value)} placeholder="Es. @rossiarch.it" className={inputClass} style={inputStyle} />
              </div>
            </div>
          </div>
          <div className="rounded-xl p-4 space-y-3" style={{ background: 'hsl(40,18%,97%)', border: '1px solid #D9D2C8' }}>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Azione</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Imposta categoria</label>
                <select data-testid="rule-action-value" value={form.action_value} onChange={e => field('action_value', e.target.value)} className={inputClass} style={inputStyle}>
                  <option value="">-- Seleziona --</option>
                  {customCategories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Priorità (0-10)</label>
                <input data-testid="rule-priority" type="number" min="0" max="10" value={form.priority} onChange={e => field('priority', parseInt(e.target.value) || 0)} className={inputClass} style={inputStyle} />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <input data-testid="apply-to-all-checkbox" type="checkbox" checked={applyToAll} onChange={e => setApplyToAll(e.target.checked)} className="rounded" />
              <span className="text-gray-600">Applica subito a tutte le email esistenti</span>
            </label>
          </div>
          <div className="flex items-center gap-3 pt-1">
            <button data-testid="save-rule-btn" onClick={handleCreate} disabled={saving} className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white rounded-xl transition-all" style={{ background: '#E2725B', opacity: saving ? 0.7 : 1 }}>
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
              {saving ? 'Salvataggio...' : 'Crea regola'}
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {rules.map(rule => {
          const cat = customCategories.find(c => c.id === rule.action_value);
          return (
            <div key={rule.id} data-testid={`rule-item-${rule.id}`} className="flex items-center justify-between p-4 rounded-xl border transition-all" style={{ borderColor: '#D9D2C8', background: 'white', opacity: rule.active ? 1 : 0.6 }}>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: cat ? cat.bg_color : '#F0F2F3' }}>
                  <Filter size={14} style={{ color: cat ? cat.color : '#7F8C8D' }} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{rule.name}</span>
                    {rule.priority > 0 && <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: '#EDE8FF', color: '#6B6FD8' }}>P{rule.priority}</span>}
                    {!rule.active && <span className="text-xs text-gray-400">disattivata</span>}
                  </div>
                  <p className="text-xs text-gray-400">{getRuleDescription(rule)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid={`toggle-rule-${rule.id}`} onClick={() => handleToggle(rule)} disabled={togglingId === rule.id} className="text-gray-400 hover:text-gray-600 transition-colors p-1" title={rule.active ? 'Disattiva' : 'Attiva'}>
                  {togglingId === rule.id ? <Loader2 size={16} className="animate-spin" /> : (rule.active ? <ToggleRight size={18} style={{ color: '#2DAA7E' }} /> : <ToggleLeft size={18} />)}
                </button>
                <button data-testid={`delete-rule-${rule.id}`} onClick={() => handleDelete(rule.id, rule.name)} disabled={deletingId === rule.id} className="p-1.5 text-gray-400 hover:text-red-500 transition-colors rounded-lg">
                  {deletingId === rule.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                </button>
              </div>
            </div>
          );
        })}
        {rules.length === 0 && (
          <div className="text-center py-10 text-gray-400">
            <Filter size={28} className="mx-auto mb-3 opacity-40" />
            <p className="text-sm">Nessuna regola configurata</p>
            <p className="text-xs mt-1">Crea una regola per categorizzare automaticamente le email</p>
          </div>
        )}
      </div>
    </div>
  );
}
